import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  Network,
  Clock4,
  Maximize2,
  Recycle,
  Handshake,
} from "lucide-react";
import { FEATURES } from "@/constants/testIds";

const rows = [
  {
    id: "capacity",
    eyebrow: "Access to capacity",
    title: "One call away from the entire Southern African road network.",
    desc:
      "Rather than chasing carriers one by one, you tap into our 1,200+ vetted operators through a single point of contact. We find the truck — you keep moving.",
    bullets: [
      "Single point of contact for every load",
      "1,200+ active, compliant operators",
      "Same-day matching on most major lanes",
    ],
    icon: Network,
    img: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    badge: { label: "Carriers in network", value: "1,200+", trend: "and growing weekly" },
  },
  {
    id: "ease",
    eyebrow: "Ease of doing business",
    title: "Time-efficient load distribution — anywhere in the region.",
    desc:
      "We've spent years simplifying what used to be the messy middle of freight. From quote to POD, the workflow is built around your time, not ours.",
    bullets: [
      "Rapid quotes — usually within the hour",
      "Single invoice for multi-leg moves",
      "Proactive updates, not chase calls",
    ],
    icon: Clock4,
    img: "https://images.pexels.com/photos/10359129/pexels-photo-10359129.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
    badge: { label: "Avg. quote turnaround", value: "<1 hr", trend: "on standard lanes" },
  },
  {
    id: "scalability",
    eyebrow: "Scalability when you need it",
    title: "Scale up — or down — with your demand cycles.",
    desc:
      "Seasonal peaks, ad-hoc projects or sudden volume spikes? Our shared network flexes with you. No fleet to invest in, no operators to onboard.",
    bullets: [
      "Surge capacity for peak seasons",
      "Multi-truck convoys for project work",
      "No long-term contracts required",
    ],
    icon: Maximize2,
    img: "https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    badge: { label: "Peak surge", value: "+340%", trend: "absorbed in 48hrs" },
  },
  {
    id: "shared",
    eyebrow: "Shared economy",
    title: "Smarter freight through industry collaboration.",
    desc:
      "By pooling demand across industries and lanes, we cut empty kilometres, reduce cost per load, and shrink the carbon footprint of every consignment.",
    bullets: [
      "Backhaul matching reduces empty kms",
      "Cross-industry consolidations",
      "Lower cost-per-tonne, lower CO₂",
    ],
    icon: Recycle,
    img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    badge: { label: "Empty-km reduction", value: "−27%", trend: "vs industry avg." },
  },
  {
    id: "single",
    eyebrow: "A single supplier",
    title: "One partner. One invoice. Zero supplier admin.",
    desc:
      "Stop juggling rate cards, schedules and statements from a dozen operators. We deal with all of them so you only deal with us.",
    bullets: [
      "Consolidated monthly invoicing",
      "Single contract & SLA",
      "One number for every load",
    ],
    icon: Handshake,
    img: "https://images.unsplash.com/photo-1586528116022-aeda1613c63d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    badge: { label: "Suppliers replaced", value: "1 of many", trend: "us — instead of them all" },
  },
];

export default function PlatformFeatures() {
  return (
    <section
      id="shippers"
      data-testid={FEATURES.section}
      className="relative py-24 md:py-32 bg-[#F8FAFC] border-y border-[#E2E8F0]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
            Why shippers choose us
          </div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
            The brokerage that fits your needs.
          </h2>
          <p className="mt-5 text-lg text-[#1F2937]/75 leading-relaxed">
            Five reasons our shippers stay with us — and refer us. None of them
            involve software you have to learn.
          </p>
        </div>

        <div className="mt-16 md:mt-20 flex flex-col gap-20 md:gap-28">
          {rows.map((r, i) => {
            const reverse = i % 2 === 1;
            const Icon = r.icon;
            return (
              <div
                key={r.id}
                data-testid={FEATURES.row(r.id)}
                className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <motion.div
                  initial={{ opacity: 0, x: reverse ? 40 : -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-6"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0]">
                    <Icon className="h-3.5 w-3.5" /> {r.eyebrow}
                  </div>
                  <h3 className="font-display mt-5 text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter text-[#002050] leading-[1.1]">
                    {r.title}
                  </h3>
                  <p className="mt-5 text-lg text-[#1F2937]/75 leading-relaxed">
                    {r.desc}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {r.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <span className="mt-0.5 h-6 w-6 flex items-center justify-center rounded-md bg-[#0070C0]/10">
                          <Check className="h-3.5 w-3.5 text-[#0070C0]" strokeWidth={3} />
                        </span>
                        <span className="text-[15px] text-[#1F2937]">{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7 }}
                  className="lg:col-span-6 relative"
                >
                  <div className="relative rounded-3xl overflow-hidden border border-white shadow-[0_30px_80px_-25px_rgba(0,32,80,0.35)]">
                    <img
                      src={r.img}
                      alt={r.title}
                      className="w-full h-[360px] md:h-[460px] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#002050]/40 via-transparent to-[#00AEEF]/20" />

                    <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[280px] rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-lg p-4">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0070C0]">
                        {r.badge.label}
                      </div>
                      <div className="mt-1 font-display text-3xl font-extrabold text-[#002050] tracking-tighter">
                        {r.badge.value}
                      </div>
                      <div className="text-xs text-[#1F2937]/70">
                        {r.badge.trend}
                      </div>
                    </div>
                  </div>
                  <div className="absolute -z-10 -bottom-6 -right-6 h-44 w-44 rounded-full bg-[#00AEEF]/25 blur-3xl" />
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
